import {useCallback} from 'react';

import {PROJECT_TAGS} from '@/constants/ProjectsConstants';
import {useTrackingManager} from '@/hooks';
import {
  IEditInfoDataDefinition,
  IErrorDataDefinition,
} from '@/services/segment/models/TrackingManagerEventModel';

import {EDIT_INFO_EVENTS} from './constants';

interface IEditInfoTrackEventProps {
  event: string;
  editInfo?: IEditInfoDataDefinition;
  error?: IErrorDataDefinition;
}

interface IEditInfoTrackersProps {
  parentId?: string;
  familyMemberId?: string;
}

export const useEditInfoTrackers = (props: IEditInfoTrackersProps) => {
  const {parentId, familyMemberId} = props;
  const {trackEvent} = useTrackingManager();

  const trackEditInfoEvent = useCallback(
    (props: IEditInfoTrackEventProps) => {
      const {event, editInfo, error} = props;
      trackEvent(event, {
        project_tag: PROJECT_TAGS.EDIT_INFO,
        ...(parentId && {user: {id: parentId}}),
        ...(familyMemberId && {FamilyMember: {id: familyMemberId}}),
        ...(editInfo && {EditInfo: editInfo}),
        ...(error && {error}),
      });
    },
    [trackEvent, parentId, familyMemberId],
  );

  const trackViewEditInfoNotRequested = useCallback(() => {
    trackEditInfoEvent({
      event: EDIT_INFO_EVENTS.view_edit_info_not_requested,
    });
  }, [trackEditInfoEvent]);

  const trackViewEditInfoApproved = useCallback(() => {
    trackEditInfoEvent({
      event: EDIT_INFO_EVENTS.view_edit_info_approved,
    });
  }, [trackEditInfoEvent]);

  const trackViewEditInfoError = useCallback(() => {
    trackEditInfoEvent({
      event: EDIT_INFO_EVENTS.view_edit_info_error,
    });
  }, [trackEditInfoEvent]);

  const trackViewEditInfoErrorNoData = useCallback(() => {
    trackEditInfoEvent({
      event: EDIT_INFO_EVENTS.view_edit_info_error_no_data,
    });
  }, [trackEditInfoEvent]);

  const trackViewEditInfoExpired = useCallback(() => {
    trackEditInfoEvent({
      event: EDIT_INFO_EVENTS.view_edit_info_expired,
    });
  }, [trackEditInfoEvent]);

  const trackSelectEditInfoResetPassword = useCallback(() => {
    trackEditInfoEvent({
      event: EDIT_INFO_EVENTS.select_edit_info_reset_password,
    });
  }, [trackEditInfoEvent]);

  const trackSelectEditInfoBackToApp = useCallback(() => {
    trackEditInfoEvent({
      event: EDIT_INFO_EVENTS.select_edit_info_back_to_app,
    });
  }, [trackEditInfoEvent]);

  const trackSelectEditInfoBackToAppFromExpired = useCallback(() => {
    trackEditInfoEvent({
      event: EDIT_INFO_EVENTS.select_edit_info_back_to_app_from_expired,
    });
  }, [trackEditInfoEvent]);

  const trackApiEditInfoEmailOtpSuccess = useCallback(
    (switchableAccount?: boolean) => {
      trackEditInfoEvent({
        event: EDIT_INFO_EVENTS.api_edit_info_email_otp_success,
        ...(switchableAccount !== undefined && {
          editInfo: {switchable: {account: switchableAccount}},
        }),
      });
    },
    [trackEditInfoEvent],
  );

  const trackSelectEditInfoInviteKid = useCallback(
    (switchableAccount?: boolean) => {
      trackEditInfoEvent({
        event: EDIT_INFO_EVENTS.select_edit_info_invite_kid,
        ...(switchableAccount !== undefined && {
          editInfo: {switchable: {account: switchableAccount}},
        }),
      });
    },
    [trackEditInfoEvent],
  );

  const trackShareEditInfoInviteKidSuccess = useCallback(
    (switchableAccount?: boolean) => {
      trackEditInfoEvent({
        event: EDIT_INFO_EVENTS.share_edit_info_invite_kid_success,
        ...(switchableAccount !== undefined && {
          editInfo: {switchable: {account: switchableAccount}},
        }),
      });
    },
    [trackEditInfoEvent],
  );

  const trackApiEditInfoEmailOtpError = useCallback(
    (message?: string, code?: string) => {
      trackEditInfoEvent({
        event: EDIT_INFO_EVENTS.api_edit_info_email_otp_error,
        error: {
          code: code ?? 'unknown_error',
          message: message ?? 'An unknown error occurred',
        },
      });
    },
    [trackEditInfoEvent],
  );

  const trackSelectEditInfoRepeatProcess = useCallback(
    (switchableAccount?: boolean) => {
      trackEditInfoEvent({
        event: EDIT_INFO_EVENTS.select_edit_info_repeat_process,
        ...(switchableAccount !== undefined && {
          editInfo: {switchable: {account: switchableAccount}},
        }),
      });
    },
    [trackEditInfoEvent],
  );

  return {
    trackViewEditInfoNotRequested,
    trackViewEditInfoApproved,
    trackViewEditInfoError,
    trackViewEditInfoErrorNoData,
    trackViewEditInfoExpired,
    trackSelectEditInfoResetPassword,
    trackSelectEditInfoBackToApp,
    trackSelectEditInfoBackToAppFromExpired,
    trackApiEditInfoEmailOtpSuccess,
    trackSelectEditInfoInviteKid,
    trackShareEditInfoInviteKidSuccess,
    trackApiEditInfoEmailOtpError,
    trackSelectEditInfoRepeatProcess,
  };
};
